import { BookmarksContext } from "context/bookmarks";
import React, { memo, useState, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import Tabs from '@material-ui/core/Tabs';
import Tab from '@material-ui/core/Tab';
import Box from '@material-ui/core/Box';
import Icons from 'components/icons';
import {
  NAVIGATION_TABS,
  getNavigationTabOptions,
} from 'components/ebook-reader/ebook-reader.utils';
import { queuePropType } from "utils/prop-types";
import useStyles from './navigation-menu.styles';
import { getTOCItems } from './navigation-menu.utils';
import { READER_THEMES } from "utils/themes";
import NavigationMenuWrapper from "components/common/navigation-menu-wrapper";
import ChaptersTabPanel from "components/ebook-reader/components/navigation-menu/chapters-tab.component";
import BookmarksTabPanel from "components/ebook-reader/components/navigation-menu/bookmarks-tab.component";


const TAB_OPTIONS = getNavigationTabOptions([
  NAVIGATION_TABS.chapters,
  NAVIGATION_TABS.bookmarks,
]);


const NavigationMenu = memo((props) => {
  const { queue, activeIndex, pdf, controlBtnProps, updateEBookDataAction } = props;
  const { assetId } = queue[activeIndex] ?? {};

  const classes = useStyles({ activeTheme: READER_THEMES.light });
  const { t } = useTranslation();

  const [tocItems, setTocItems] = useState([]);
  const [activeTab, setActiveTab] = useState(NAVIGATION_TABS.chapters);
  const [isTocLoading, setIsTocLoading] = useState(false);

  const { loadBookmarks } = useContext(BookmarksContext);

  useEffect(() => {
    if (pdf) {
      setIsTocLoading(true);
      getTOCItems(pdf).then(toc => {
        setTocItems(toc);
        setIsTocLoading(false);
      });
    }
    loadBookmarks(assetId);
  }, [pdf, assetId]);

  const handleNavItemClick = useCallback(
    (location) => () => {
      updateEBookDataAction({ currentPage: location.locations.position });
    },
    [],
  );

  const handleTabChange = useCallback((_, value) => {
    setActiveTab(value);
  }, [setActiveTab]);

  const mapChapter = useCallback((item) => {
    return {
      selector: item.pageNumber,
      page: item.pageNumber,
      title: item.title,
      id: `chapter-${item.pageNumber}-${item.title}`,
      ...item,
    };
  }, []);

  return (
  <NavigationMenuWrapper
    controlBtnProps={controlBtnProps}
    controlBtnIcon={Icons.ReaderTOC}
    paperProps={{ className: classes.paper }}
  >
    <Box className={classes.container}>
      <Box className={classes.tabHeader}>
        <Tabs
          className={classes.tabs}
          indicatorColor="primary"
          variant="fullWidth"
          value={activeTab}
          onChange={handleTabChange}
          TabIndicatorProps={{ className: classes.tabIndicator }}
        >
          {TAB_OPTIONS.map(({ value, label, ...restProps }) => (
            <Tab key={value} value={value} label={t(label)} {...restProps} />
          ))}
        </Tabs>
      </Box>
      {TAB_OPTIONS.map(({ value: tab }) => {
        if (tab === NAVIGATION_TABS.chapters) {
          return <ChaptersTabPanel
              classes={classes}
              activeTab={activeTab}
              isLoading={isTocLoading}
              items={tocItems.map(mapChapter)}
              handleNavItemClick={handleNavItemClick}
            />
        }
        if (tab === NAVIGATION_TABS.bookmarks) {
          return <BookmarksTabPanel
              activeTab={activeTab}
              classes={classes}
              handleNavItemClick={handleNavItemClick}
          />
        }
      })}
    </Box>
  </NavigationMenuWrapper>
  );
});

NavigationMenu.propTypes = {
  queue: queuePropType,
  activeIndex: PropTypes.number.isRequired,
  updateEBookDataAction: PropTypes.func.isRequired,
  pdf: PropTypes.shape({}),
  controlBtnProps: PropTypes.shape({
    className: PropTypes.string,
    iconClassName: PropTypes.string,
  }),
};


export default NavigationMenu;
